import React from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import {
  FileText, Search, Star, Layout, Briefcase, Send, Shield, CheckCircle,
  TrendingUp, Calendar, ArrowRight, X, Check,
} from "react-feather";
import { Navbar } from "./Navbar";

/* The features page. Its one job is CLARITY: a visitor should leave knowing
   exactly what the product does, in what order, under what rules, and what it
   costs. Organized by the user's journey rather than a flat grid. */

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.5, ease: "easeOut" },
};

const PILLARS = [
  {
    step: "01",
    name: "Build",
    headline: "A resume that gets past the software",
    intro: "Most resumes are rejected by screening software before a person ever reads them. This is where we fix that.",
    link: { to: "/upload", label: "Try it with your resume" },
    features: [
      {
        icon: FileText,
        title: "AI tailoring, one job at a time",
        desc: "Upload your resume once, then paste any job description. The AI rewrites your bullet points to speak that job's language, using only your real experience. It never invents an employer, a date, a metric, or a skill.",
      },
      {
        icon: Search,
        title: "Keyword analysis",
        desc: "We compare your resume to the job description and show you exactly which keywords matched and which are missing, so you know why your score is what it is.",
      },
      {
        icon: Star,
        title: "An honest, repeatable score",
        desc: "Your match score is measured the way a real tracking system reads a resume: by checking that the job's keywords are actually present. The same method is used before and after tailoring, so the improvement you see is real.",
      },
      {
        icon: Layout,
        title: "Four professional templates",
        desc: "Modern, Classic, Minimal, and Executive. Every template parses cleanly in applicant tracking systems and downloads as a polished PDF.",
      },
    ],
  },
  {
    step: "02",
    name: "Apply",
    headline: "Applications without the grind",
    intro: "Auto-Apply handles the repetitive work of applying. You stay in charge of the one part that matters: the decision to send.",
    link: { to: "/auto-apply", label: "Open Auto-Apply" },
    features: [
      {
        icon: Briefcase,
        title: "A live job feed, ranked for you",
        desc: "Over a thousand live postings from Greenhouse, Lever, and Ashby company boards, United States only, scored against your resume so your best fits rise to the top. One click refreshes the whole feed.",
      },
      {
        icon: Send,
        title: "One click fills the whole application",
        desc: "Click Apply and a Chrome window opens with the employer's real form filled in: your contact details, their screening questions, essay answers drafted in your own voice, and your tailored resume attached.",
      },
      {
        icon: Shield,
        title: "You always press Submit",
        desc: "Nothing is ever sent by a machine behind your back. You review the finished form and press the employer's own Submit button yourself. That keeps every application legitimate, and it is why they land.",
      },
      {
        icon: CheckCircle,
        title: "A tracker with real proof",
        desc: "Every application is recorded with the employer's own confirmation, a screenshot of the final page, and an email receipt. Failures are recorded honestly and the credit comes back automatically.",
      },
    ],
  },
  {
    step: "03",
    name: "Grow",
    headline: "Know where you stand",
    intro: "Applying is half the work. The other half is walking into the interview prepared.",
    link: { to: "/appointment", label: "Book a session" },
    features: [
      {
        icon: TrendingUp,
        title: "Interview-fit score per application",
        desc: "Each submission is scored against its job description: what matched, what is missing, and where your strongest talking points are.",
      },
      {
        icon: Calendar,
        title: "One-on-one career coaching",
        desc: "Book a session with a real advisor for resume review, interview practice, and search strategy, on a real calendar.",
      },
    ],
  },
];

const NEVER = [
  "Invent an employer, date, metric, or skill on your behalf",
  "Answer questions about race, gender, disability, or veteran status",
  "Send an application without your click",
  "Solve captchas or work around employer security",
  "Sell your personal information",
];

const ALWAYS = [
  "Fill every answer from your real history and profile",
  "Show you every drafted answer on the form before you send",
  "Record the true outcome of every application, including failures",
  "Refund the credit automatically when nothing was sent",
  "Leave the final click in your hands",
];

const FAQS = [
  {
    q: "Can it apply to jobs while I am away?",
    a: "No, and that is deliberate. The filling is automatic, but the sending is yours. Every application waits for you to review it and press Submit.",
  },
  {
    q: "Where do the jobs come from?",
    a: "Public job boards run on Greenhouse, Lever, and Ashby, filtered to United States positions and re-crawled whenever you press Refresh.",
  },
  {
    q: "What does Auto-Apply cost?",
    a: "$24.99 for 1,000 application credits. A credit is spent only when an application is actually sent. If a run fails before sending, the credit returns automatically. Resume tools are priced separately on the Pricing page.",
    link: { to: "/pricing", label: "See all pricing" },
  },
  {
    q: "What if a form asks something you cannot answer?",
    a: "The agent leaves it blank and tells you, rather than guessing. Answer it once and it is saved, so every later application that asks the same question is covered.",
  },
  {
    q: "Is my information sold or shared?",
    a: "Never sold. Your application goes to the employer you chose, payments go through Stripe, and drafting uses an AI provider under contract. The full list is in our Privacy Policy.",
    link: { to: "/privacy", label: "Read the Privacy Policy" },
  },
  {
    q: "Do you guarantee interviews?",
    a: "No one honestly can. We remove the grind and improve your odds; hiring decisions belong to employers.",
  },
];

function Features() {
  const navigate = useNavigate();
  return (
    <>
      <Navbar />
      <div className="bg-white">

        {/* Header */}
        <div className="bg-gradient-to-b from-sky-50 via-blue-50/40 to-white">
          <div className="max-w-5xl mx-auto px-6 pt-14 pb-12 text-center">
            <motion.h1 className="text-4xl sm:text-5xl font-extrabold text-gray-900 tracking-tight"
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
              What Resume Advisor does
            </motion.h1>
            <motion.p className="mt-4 text-lg text-gray-600 max-w-2xl mx-auto"
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.1 }}>
              One platform takes you from a resume to submitted applications.
              Here is exactly what each part does, in the order you will use it.
            </motion.p>
          </div>
        </div>

        {/* The three pillars, in journey order */}
        <div className="max-w-5xl mx-auto px-6">
          {PILLARS.map(({ step, name, headline, intro, features, link }) => (
            <motion.section key={step} className="py-12 border-b border-gray-100 last:border-0" {...fadeUp}>
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <div className="flex items-baseline gap-3">
                  <span className="text-sm font-extrabold text-blue-600 tracking-widest">{step}</span>
                  <span className="text-sm font-bold text-gray-400 uppercase tracking-widest">{name}</span>
                </div>
                {link && (
                  <button
                    onClick={() => navigate(link.to)}
                    className="inline-flex items-center gap-1.5 text-sm font-bold text-blue-600 hover:gap-2.5 transition-all"
                  >
                    {link.label} <ArrowRight className="w-4 h-4" />
                  </button>
                )}
              </div>
              <h2 className="mt-2 text-2xl sm:text-3xl font-extrabold text-gray-900 tracking-tight">{headline}</h2>
              <p className="mt-2 text-gray-600 max-w-2xl">{intro}</p>
              <div className="mt-8 grid sm:grid-cols-2 gap-x-10 gap-y-8">
                {features.map(({ icon: Icon, title, desc }) => (
                  <div key={title} className="flex gap-4">
                    <span className="flex-shrink-0 inline-flex w-10 h-10 rounded-xl bg-blue-50 text-blue-600 items-center justify-center">
                      <Icon className="w-5 h-5" />
                    </span>
                    <div>
                      <h3 className="font-bold text-gray-900">{title}</h3>
                      <p className="mt-1 text-sm text-gray-600 leading-relaxed">{desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </motion.section>
          ))}
        </div>

        {/* The rules we operate by */}
        <div className="bg-gradient-to-br from-gray-900 via-blue-950 to-gray-900 text-white mt-6">
          <div className="max-w-5xl mx-auto px-6 py-14">
            <motion.div className="text-center max-w-2xl mx-auto" {...fadeUp}>
              <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight">The rules we operate by</h2>
              <p className="mt-2 text-blue-100/80 text-sm">
                Automation you can trust has to be explicit about its limits. These are ours, and they are enforced in the software, not just promised here.
              </p>
            </motion.div>
            <div className="mt-10 grid md:grid-cols-2 gap-5">
              <motion.div className="bg-white/5 border border-white/10 rounded-2xl p-6" {...fadeUp}>
                <h3 className="font-bold text-red-300 flex items-center gap-2"><X className="w-4 h-4" /> We never</h3>
                <ul className="mt-4 space-y-3">
                  {NEVER.map((line) => (
                    <li key={line} className="flex gap-2.5 text-sm text-blue-100/90">
                      <X className="w-4 h-4 text-red-400/70 flex-shrink-0 mt-0.5" /> {line}
                    </li>
                  ))}
                </ul>
              </motion.div>
              <motion.div className="bg-white/5 border border-white/10 rounded-2xl p-6" {...fadeUp}>
                <h3 className="font-bold text-green-300 flex items-center gap-2"><Check className="w-4 h-4" /> We always</h3>
                <ul className="mt-4 space-y-3">
                  {ALWAYS.map((line) => (
                    <li key={line} className="flex gap-2.5 text-sm text-blue-100/90">
                      <Check className="w-4 h-4 text-green-400/80 flex-shrink-0 mt-0.5" /> {line}
                    </li>
                  ))}
                </ul>
              </motion.div>
            </div>
          </div>
        </div>

        {/* Straight answers */}
        <div className="max-w-5xl mx-auto px-6 py-14">
          <motion.h2 className="text-2xl sm:text-3xl font-extrabold text-gray-900 tracking-tight text-center" {...fadeUp}>
            Straight answers
          </motion.h2>
          <div className="mt-8 grid md:grid-cols-2 gap-5">
            {FAQS.map(({ q, a, link }) => (
              <motion.div key={q} className="bg-white border border-gray-200 rounded-2xl p-5" {...fadeUp}>
                <h3 className="font-bold text-gray-900">{q}</h3>
                <p className="mt-1.5 text-sm text-gray-600 leading-relaxed">{a}</p>
                {link && (
                  <button
                    onClick={() => navigate(link.to)}
                    className="mt-2 inline-flex items-center gap-1.5 text-sm font-bold text-blue-600 hover:gap-2.5 transition-all"
                  >
                    {link.label} <ArrowRight className="w-4 h-4" />
                  </button>
                )}
              </motion.div>
            ))}
          </div>
        </div>

        {/* Close */}
        <div className="max-w-5xl mx-auto px-6 pb-16">
          <motion.div className="bg-gradient-to-r from-blue-600 to-sky-500 rounded-3xl px-8 py-10 text-center text-white shadow-xl shadow-blue-600/20" {...fadeUp}>
            <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight">See it work on your own resume.</h2>
            <div className="mt-6 flex flex-wrap justify-center gap-3">
              <button
                onClick={() => navigate("/upload")}
                className="inline-flex items-center gap-2 bg-white text-blue-700 font-bold px-6 py-3 rounded-xl hover:bg-blue-50 transition"
              >
                Start with my resume <ArrowRight className="w-4 h-4" />
              </button>
              <button
                onClick={() => navigate("/pricing")}
                className="inline-flex items-center gap-2 bg-white/10 border border-white/30 text-white font-semibold px-6 py-3 rounded-xl hover:bg-white/20 transition"
              >
                See pricing
              </button>
            </div>
          </motion.div>
        </div>
      </div>
    </>
  );
}

export default Features;
